import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { TimezoneIcon } from '~/modules/common/components/Icons';
import { LoadingButton } from '~/modules/common/components';
import { useUpdateUserTimeZone } from '~/modules/resource-management/resourceUserDetails/hooks';
import { UserTimeZoneDropdown } from './UserTimeZoneDropdown';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  }
}));

export const EditUserTimeZoneDialog = ({
  open,
  onClose,
  userUri,
  currentTimeZone
}) => {
  const classes = useStyles();
  const [selectedTimeZone, setSelectedTimeZone] = useState(currentTimeZone);

  const { updateUserTimeZone, loading } = useUpdateUserTimeZone({
    userUri,
    onClose
  });

  const handleClose = useCallback(() => {
    setSelectedTimeZone(currentTimeZone);
    onClose();
  }, [onClose, currentTimeZone]);

  const handleSubmit = useCallback(() => {
    updateUserTimeZone(selectedTimeZone?.id);
  }, [updateUserTimeZone, selectedTimeZone]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <TimezoneIcon className={classes.titleIcon} />
        <Typography variant="h6">
          <FormattedMessage id="resourceBasicInfoCard.timeZone" />
        </Typography>
      </DialogTitle>
      <DialogContent>
        <UserTimeZoneDropdown
          onValueChange={setSelectedTimeZone}
          selectedTimeZone={selectedTimeZone}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          <FormattedMessage id="button.cancel" />
        </Button>
        <LoadingButton
          color="primary"
          onClick={handleSubmit}
          isLoading={loading}
        >
          <FormattedMessage id="button.save" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EditUserTimeZoneDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userUri: PropTypes.string.isRequired,
  currentTimeZone: PropTypes.shape({
    id: PropTypes.string,
    displayText: PropTypes.string
  })
};
